import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Logo, { LogoMark } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function AuthShell({ children }) {
  const { t } = useI18n();
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(150deg,#1A73E8 0%,#0D3C78 100%)" }}>
        <Logo size={40} withText={false} variant="white" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <LogoMark size={44} variant="white" />
            <h1 className="font-head text-4xl font-extrabold leading-tight">DiabetFlow</h1>
          </div>
          <p className="text-blue-100 text-lg mt-1">Rotina Organizada</p>
          <p className="text-blue-50 text-xl mt-6 max-w-md leading-relaxed">{t("brand_message")}</p>
        </div>
        <div className="absolute -right-16 -bottom-16 h-80 w-80 rounded-full bg-white/10" />
        <div className="absolute right-24 top-20 h-40 w-40 rounded-full bg-white/10" />
      </div>
      <div className="flex items-center justify-center p-6 bg-[#F4F8FC]">
        <div className="w-full max-w-sm df-rise">
          <div className="lg:hidden mb-8 flex justify-center"><Logo /></div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.onboarded ? "/app" : "/onboarding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell>
      <h2 className="font-head text-2xl font-bold text-[#0D253F]">{t("login_title")}</h2>
      <p className="text-[#627D98] mt-1 mb-6">{t("login_sub")}</p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>{t("email")}</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
        </div>
        <div>
          <Label>{t("password")}</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
        </div>
        <Button type="submit" disabled={loading} className="w-full h-12 bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold" data-testid="login-submit">
          {loading ? t("loading") : t("login")}
        </Button>
      </form>
      <div className="mt-6 text-center text-sm text-[#627D98]">
        {t("no_account")}{" "}
        <Link to="/register" className="text-[#1A73E8] font-semibold hover:underline" data-testid="to-register">
          {t("create_access")}
        </Link>
      </div>
    </AuthShell>
  );
}
